import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { FC, PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';

import { isLoaderVisibleSelector, OverlayLoader } from '@xarvio/elements';

const OVERLAY_SHOW_DELAY_MS = 250;
const OVERLAY_MINIMUM_VISIBLE_MS = 400;

type RegisterLoadingFallback = () => () => void;

interface FullPageLoadingOverlayProviderProps extends PropsWithChildren {
  isOverlayEnabled?: boolean;
}

const FullPageLoadingOverlayContext =
  createContext<RegisterLoadingFallback | null>(null);

export const FullPageLoadingOverlayProvider: FC<
  FullPageLoadingOverlayProviderProps
> = ({ children, isOverlayEnabled = true }) => {
  const isGlobalLoaderVisible = useSelector(isLoaderVisibleSelector);
  const [loadingFallbackCount, setLoadingFallbackCount] = useState(0);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const visibleSinceRef = useRef<number | undefined>(undefined);

  const registerLoadingFallback = useCallback(() => {
    setLoadingFallbackCount((count) => count + 1);

    return () => {
      setLoadingFallbackCount((count) => count - 1);
    };
  }, []);

  const isLoading = isGlobalLoaderVisible || loadingFallbackCount > 0;

  useEffect(() => {
    if (!isOverlayEnabled) {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = undefined;
      }

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = undefined;
      }

      visibleSinceRef.current = undefined;
      setIsOverlayVisible(false);
      return;
    }

    if (isLoading) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = undefined;
      }

      if (!isOverlayVisible && !showTimerRef.current) {
        showTimerRef.current = setTimeout(() => {
          showTimerRef.current = undefined;
          visibleSinceRef.current = Date.now();
          setIsOverlayVisible(true);
        }, OVERLAY_SHOW_DELAY_MS);
      }

      return;
    }

    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = undefined;
    }

    if (!isOverlayVisible) {
      return;
    }

    const remainingVisibleTime = Math.max(
      0,
      OVERLAY_MINIMUM_VISIBLE_MS -
        (Date.now() - (visibleSinceRef.current ?? 0)),
    );

    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = undefined;
      visibleSinceRef.current = undefined;
      setIsOverlayVisible(false);
    }, remainingVisibleTime);
  }, [isLoading, isOverlayEnabled, isOverlayVisible]);

  useEffect(() => {
    return () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  return (
    <FullPageLoadingOverlayContext value={registerLoadingFallback}>
      {children}
      {isOverlayVisible && <OverlayLoader />}
    </FullPageLoadingOverlayContext>
  );
};

export const FullPageLoadingOverlayFallback = () => {
  const registerLoadingFallback = use(FullPageLoadingOverlayContext);

  if (!registerLoadingFallback) {
    throw new Error(
      'FullPageLoadingOverlayFallback must be used within FullPageLoadingOverlayProvider',
    );
  }

  useEffect(() => registerLoadingFallback(), [registerLoadingFallback]);

  return null;
};
